import { motion } from "motion/react";
import React from "react";
import { useReducedMotion } from "../../../hooks/useReducedMotion.hook";
import { isUiMotionEnabled } from "../../shared/motion/uiMotion.tools";
import {
    buildSavedItineraryListItemEnterTransition,
    buildSavedItineraryListItemExitState,
    buildSavedItineraryListItemExitTransition,
    buildSavedItineraryListItemInitialState,
    buildSavedItineraryListItemLayoutTransition,
    buildSavedItineraryListItemTemporalInitialState,
    buildSavedItineraryListItemVisibleState,
    SAVED_ITINERARY_LIST_ITEM_SPACING_CLASS_NAME,
    type SavedItineraryListItemAnimationProfile,
} from "./savedItineraryList.animation.tools";

interface SavedItineraryListItemMotionProps {

    /** Zero-based index in the displayed saved list (drives enter stagger). */
    "index": number;

    /** Row animation profile (`temporal` for past-toggle show/hide). */
    "animationProfile"?: SavedItineraryListItemAnimationProfile;

    /** Itinerary card content. */
    "children": React.ReactNode;
}

/**
 * Animated shell for a saved-itinerary list row.
 *
 * Enter uses itinerary-panel stagger; exit collapses height so siblings reflow smoothly.
 * Layout projection pushes upcoming rows down when past itineraries are revealed.
 *
 * @param props Row motion props.
 * @returns Animated row shell or static wrapper when motion is disabled.
 */
const SavedItineraryListItemMotion: React.FC<SavedItineraryListItemMotionProps> = ( {
    animationProfile = "default",
    index,
    children,
} ) => {
    const reducedMotion = useReducedMotion();
    const animationsEnabled = isUiMotionEnabled( reducedMotion );

    if ( !animationsEnabled ) {
        return (
            <div className={SAVED_ITINERARY_LIST_ITEM_SPACING_CLASS_NAME}>
                {children}
            </div>
        );
    }

    const exitTransition = buildSavedItineraryListItemExitTransition( reducedMotion, animationProfile );
    const enterTransition = buildSavedItineraryListItemEnterTransition( index, reducedMotion, animationProfile );
    const layoutTransition = buildSavedItineraryListItemLayoutTransition( reducedMotion );
    const initialState = animationProfile === "temporal"
        ? buildSavedItineraryListItemTemporalInitialState()
        : buildSavedItineraryListItemInitialState();

    return (
        <motion.div
            animate={buildSavedItineraryListItemVisibleState()}
            className={SAVED_ITINERARY_LIST_ITEM_SPACING_CLASS_NAME}
            exit={{
                ...buildSavedItineraryListItemExitState(),
                "transition": exitTransition,
            }}
            initial={initialState}
            layout="position"
            layoutScroll
            style={{ "overflow": "hidden" }}
            transition={{
                ...enterTransition,
                "layout": layoutTransition,
            }}
        >
            {children}
        </motion.div>
    );
};

export default SavedItineraryListItemMotion;
